import { useState } from "react";
import {
  TimelineAppointment,
  useGetUpcomingAppointmentsQuery,
} from "../../useGetUpcomingAppointments";
import {
  formatShortAppointmentDate,
  formatTimeRange,
  getBrowserTimezone,
} from "../../schedule-appointment/utils";
import { Button, Icon, Text } from "@growtherapy/sprout-ui";
import { formatUpcomingAppointments, getReferClientLink } from "../utils";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  ProviderDrawerState,
  drawerStateAtom,
  selectedClientInfoAtom,
} from "../../state";
import { scheduledPatientInformationAtom } from "../../schedule-preview/state";
import { RefreshAppointmentsCallout } from "./RefreshAppointmentsCallout";
import { mightNeedToRefreshAppointmentListAtom } from "../../schedule-appointment/state";
import { faSpinnerThird as fadSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { faRepeat } from "@fortawesome/pro-solid-svg-icons";
import { EventTypeEnum } from "../../../segment/types";
import { useSendClientInfoTabViewTrackingEvent } from "../useSendClientInfoTabViewTrackingEvent";
import { AppointmentLocation } from "../../schedule-preview/types";
import { pick } from "remeda";
import type { SelectedVisitor } from "../../state";
import { AddAppointmentButtonAndReferLink } from "./AddAppointmentButtonAndReferLink";

export function UpcomingClientAppointmentsTab({
  upcomingAppointments,
  isLoading,
  onAppointmentClick,
  onClickAddAppointment,
  onRefresh,
  isClientUserAccountPaused,
  referClientLink,
}: {
  upcomingAppointments: TimelineAppointment[];
  isLoading?: boolean;
  onAppointmentClick: (appointment: TimelineAppointment) => void;
  onClickAddAppointment: () => void;
  onRefresh?: () => void;
  isClientUserAccountPaused: boolean;
  referClientLink: string;
}) {
  const [
    mightNeedToRefreshAppointmentList,
    setMightNeedToRefreshAppointmentList,
  ] = useAtom(mightNeedToRefreshAppointmentListAtom);

  if (isLoading) {
    return (
      <div className="flex justify-center">
        <Icon
          aria-hidden={false}
          aria-label="Loading..."
          size="xl"
          className="text-lilac-700 fa-spin"
          icon={fadSpinnerThird}
        />
      </div>
    );
  }
  if (!upcomingAppointments || upcomingAppointments.length === 0) {
    return (
      <div>
        <p> No appointments scheduled.</p>
        <AddAppointmentButtonAndReferLink
          onClickAddAppointment={onClickAddAppointment}
          isClientUserAccountPaused={isClientUserAccountPaused}
          referClientLink={referClientLink}
          className="mt-4"
        />
      </div>
    );
  }

  return (
    <div>
      {mightNeedToRefreshAppointmentList && (
        <RefreshAppointmentsCallout
          onClickRefresh={() => {
            setMightNeedToRefreshAppointmentList(false);
            onRefresh?.();
          }}
        />
      )}
      <ul>
        {upcomingAppointments.map((appointment) => {
          const isRecurringAppointment =
            appointment?.recurringAppointmentGroupingId;
          return (
            <li key={appointment.id}>
              <Button
                use="unstyled"
                className="text-left px-0 py-4 w-full"
                onClick={() => {
                  onAppointmentClick(appointment);
                }}
              >
                <Text className="font-medium">
                  {formatShortAppointmentDate(appointment?.timeStart)}
                  {isRecurringAppointment && (
                    <Icon
                      aria-hidden={false}
                      aria-label="(recurring appointment)"
                      icon={faRepeat}
                      className="ml-2"
                    />
                  )}
                </Text>
                <Text className="text-neutral-700 font-normal">
                  <span className="sr-only">from</span>
                  {formatTimeRange(
                    appointment.timeStart,
                    appointment.timeEnd,
                  )}{" "}
                  {getBrowserTimezone()}
                </Text>
              </Button>
              <hr className="text-neutral-600" />
            </li>
          );
        })}
      </ul>
      <AddAppointmentButtonAndReferLink
        onClickAddAppointment={onClickAddAppointment}
        isClientUserAccountPaused={isClientUserAccountPaused}
        referClientLink={referClientLink}
        className="mt-4"
      />
    </div>
  );
}

export function UpcomingClientAppointmentsTabWrapper() {
  const [timeStart, _] = useState(new Date().toISOString());
  const selectedWaitingClient = useAtomValue(selectedClientInfoAtom);
  const setDrawerState = useSetAtom(drawerStateAtom);
  const [appointmentInfo, setAppointmentInfo] = useAtom(
    scheduledPatientInformationAtom,
  );

  const patientShortId = selectedWaitingClient?.patientShortId ?? "";

  const {
    data: upcomingAppointmentsData,
    loading,
    refetch,
  } = useGetUpcomingAppointmentsQuery(patientShortId, timeStart);

  const onAppointmentClick = (appointment: TimelineAppointment) => {
    const {
      appointmentShortId,
      patientId,
      appointmentType,
      recurringAppointmentGrouping,
    } = appointment;

    const waitingClient = (selectedWaitingClient ?? {}) as SelectedVisitor;
    const presenceFields = pick(waitingClient, [
      "status",
      "visitorUuid",
      "waitingRoomConversationSid",
      "moodCheckinAt",
      "emotions",
      "inSessionAppointmentShortId",
    ] as const);

    setDrawerState(ProviderDrawerState.EDIT_APPOINTMENT);
    setAppointmentInfo({
      ...presenceFields,

      // appointment-specific fields
      appointmentStartTime: appointment.timeStart,
      appointmentEndTime: appointment.timeEnd,
      appointmentLocation: appointment.inOffice,
      appointmentId: appointment.id,
      patientId: patientId?.toString() || "",
      appointmentType,
      recurringAppointmentGrouping,
      appointmentShortId,
      patientShortId: patientShortId,
      name: selectedWaitingClient?.name ?? appointmentInfo?.name,

      // seedling-specific fields
      patientName: selectedWaitingClient?.name ?? appointmentInfo?.name ?? "",
      pronouns: upcomingAppointmentsData?.patientChart?.pronouns ?? "",
      recurringAppointmentGroupingId:
        recurringAppointmentGrouping?.recurringAppointmentGroupingId ?? "",
      indefinite: recurringAppointmentGrouping?.indefinite ?? false,
      start: appointment.timeStart,
      end: appointment.timeEnd,
      virtual: appointment.inOffice === AppointmentLocation.Virtual,
      weeksBetween: recurringAppointmentGrouping?.weeksBetween ?? 0,
      numRecurrences: recurringAppointmentGrouping?.appointments?.length ?? 0,
    });
  };

  const appointments =
    upcomingAppointmentsData?.currentProvider?.providerAppointments?.edges;
  const isClientUserAccountPaused =
    !!upcomingAppointmentsData?.patientChart?.clientUser?.activePauseRecord;

  const upcomingAppointments = formatUpcomingAppointments(appointments);

  useSendClientInfoTabViewTrackingEvent(
    patientShortId,
    EventTypeEnum.SCHEDULE_TAB,
  );

  return (
    <UpcomingClientAppointmentsTab
      upcomingAppointments={upcomingAppointments}
      isLoading={loading}
      onAppointmentClick={onAppointmentClick}
      onClickAddAppointment={() => {
        setDrawerState(ProviderDrawerState.ADD_APPOINTMENT);
      }}
      onRefresh={refetch}
      isClientUserAccountPaused={isClientUserAccountPaused}
      referClientLink={getReferClientLink(patientShortId)}
    />
  );
}
